import React from 'react';
import { DepartmentQueuesResponse } from '@/domains/internal/dashboard/services/dashboardService';
import { RoomCard } from './RoomCard';
import { Button } from '@/shared/components/ui/Button';

interface DepartmentQueueManagementProps {
  departmentQueues: DepartmentQueuesResponse;
  selectedDepartment: string;
  onSelectDepartment: (departmentCode: string) => void;
}

export const DepartmentQueueManagement: React.FC<DepartmentQueueManagementProps> = ({
  departmentQueues,
  selectedDepartment,
  onSelectDepartment
}) => {
  // Filter departments based on selected department
  const filteredDepartments = selectedDepartment
    ? departmentQueues.department_queues.filter(dept => dept.department_code === selectedDepartment)
    : [];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Section Title */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Department Queue Management</h2>
        <p className="text-sm text-gray-600">Real-time department-specific patient queue management</p>
      </div>

      {/* Department Tabs */}
      <div className="px-6 pt-4 border-b border-gray-200">
        <div className="flex space-x-6">
          {departmentQueues.department_queues.map((dept) => (
            <Button
              key={dept.department_code}
              onClick={() => onSelectDepartment(dept.department_code)}
              variant="neutral"
              padding="pb-4"
              fontSize="text-sm"
              className={`border-b-2 !rounded-none ${selectedDepartment === dept.department_code ? '!border-orange-500 !text-orange-600 font-medium !bg-transparent' : '!border-transparent !text-gray-600 hover:!text-gray-900 font-medium !bg-transparent'}`}
            >
              {dept.department_name} ({dept.rooms.reduce((total, room) => total + room.test_entries.length, 0)})
            </Button>
          ))}
        </div>
      </div>

      {/* Dynamic Department Content */}
      {filteredDepartments.map((department) => (
        <div key={department.department_code}>
          {/* Department Header */}
          <div className="box-border content-stretch flex items-center justify-between px-[24px] py-[24px] relative w-full">
            <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
              <div className="bg-[rgba(0,131,202,0.16)] box-border content-stretch flex items-center justify-center p-[6px] relative rounded-[6px] shrink-0 size-[55px]">
                <div className="relative shrink-0 size-[26px]">
                  <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                    <path d="M13 6.5C13 6.5 5.5 10.5 5.5 15.5C5.5 18.5 8 20.5 13 20.5C18 20.5 20.5 18.5 20.5 15.5C20.5 10.5 13 6.5 13 6.5Z" fill="var(--color-info)"/>
                    <path d="M13 6.5C13 6.5 13 3 13 3C13 3 15.5 3 17 4.5C18 5.5 18 7 17 8L13 6.5Z" fill="var(--color-info)"/>
                    <path d="M13 6.5C13 6.5 13 3 13 3C13 3 10.5 3 9 4.5C8 5.5 8 7 9 8L13 6.5Z" fill="var(--color-info)"/>
                  </svg>
                </div>
              </div>
              <div className="content-stretch flex flex-col gap-[3px] items-start relative shrink-0 w-[283px]">
                <div className="content-stretch flex gap-[16px] items-center relative shrink-0">
                  <p className="font-['Roboto'] font-medium leading-[32px] relative shrink-0 text-[var(--color-text-primary)] text-[24px] text-nowrap whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
                    {department.department_name}
                  </p>
                </div>
                <div className="content-stretch flex font-['Roboto'] font-normal gap-[11px] items-center leading-[20px] relative shrink-0 text-[var(--color-text-primary)] text-[14px] text-nowrap tracking-[0.25px] w-full whitespace-pre">
                  <p className="relative shrink-0" style={{ fontVariationSettings: "'wdth' 100" }}>
                    {department.rooms.reduce((total, room) => total + room.test_entries.filter(entry => entry.status === 'waiting').length, 0)} waiting
                  </p>
                  <p className="relative shrink-0" style={{ fontVariationSettings: "'wdth' 100" }}>
                    {department.rooms.filter(room => room.test_entries.some(entry => entry.status === 'inprogress')).length} active rooms
                  </p>
                  <p className="relative shrink-0" style={{ fontVariationSettings: "'wdth' 100" }}>
                    {department.rooms.length} total rooms
                  </p>
                </div>
              </div>
            </div>
            <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
              <div className="content-stretch flex flex-col items-start justify-center relative shrink-0 w-[116px]">
                <div className="bg-[rgba(255,159,67,0.16)] box-border content-stretch flex gap-[4px] items-center justify-center min-w-[32px] px-[12px] py-[6px] relative rounded-[6px] shrink-0">
                  <div className="basis-0 flex flex-col font-['Roboto'] font-medium grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[#ff9f43] text-[13px] text-center" style={{ fontVariationSettings: "'wdth' 100" }}>
                    <p className="leading-[20px]">{department.rooms.reduce((total, room) => total + room.test_entries.filter(entry => entry.is_priority).length, 0)} need attention</p>
                  </div>
                </div>
              </div>
              <div className="bg-[var(--color-primary)] box-border content-stretch flex gap-[8px] h-[48px] items-center justify-center px-[24px] py-[12px] relative rounded-[6px] shrink-0">
                <div className="relative shrink-0 size-[24px]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-['Roboto'] font-bold leading-[24px] relative shrink-0 text-[16px] text-nowrap text-white tracking-[0.5px] whitespace-pre" style={{ fontVariationSettings: "'wdth' 100" }}>
                  Call next - All rooms
                </p>
                <div className="relative shrink-0 size-[24px]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div aria-hidden="true" className="absolute border-[#e4e4e4] border-[0px_0px_1px] border-solid inset-x-0 bottom-0 pointer-events-none" />

          {/* Room Cards */}
          <div className="px-[24px] py-[24px]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[24px]">
              {department.rooms.map((room) => (
                <RoomCard key={room.room_number} room={room} departmentCode={department.department_code} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
